import { defineStore } from "pinia";
import localforage from "localforage";
import {
  loadDonationPickupProtocols,
  loadDonationPickupCustomerData,
  recordDonationPickupItemResult,
  completeDonationPickup,
  markDonationPickupReschedulePending,
  cancelDonationPickupRequest,
} from "@/services/DonationPickupService";
import { DONATION_PICKUP_PROTOCOL_STATUSES } from "@/services/DonationPickupContracts";
import { uploadDonationPickupSignatureDataUrl } from "@/services/DonationPickupSignatureService";

// Configura o localforage para usar IndexedDB
localforage.config({
  name: "Flowmax",
  storeName: "pickup_offline_store",
  driver: localforage.INDEXEDDB,
});

// Guarda de escopo de modulo para nao registrar o listener 'online' mais de
// uma vez (sobrevive a remontagens de componentes, mas nao a reload da pagina).
let onlineListenerAttached = false;

export const usePickupOfflineStore = defineStore("pickupOffline", {
  state: () => ({
    protocols: [],
    customerDataMap: {},
    photoUrlMap: {},
    syncQueue: [],
    isSyncing: false,
    lastSyncAt: null,
  }),

  persist: {
    storage: {
      getItem: async (key) => await localforage.getItem(key),
      setItem: async (key, value) => await localforage.setItem(key, value),
      removeItem: async (key) => await localforage.removeItem(key),
    },
  },

  actions: {
    /**
     * Registra o listener de reconexao. Assim que o motorista voltar a ter
     * sinal, a fila e processada automaticamente sem precisar reabrir o app.
     */
    init() {
      if (onlineListenerAttached) return;
      if (typeof window === "undefined") return;
      onlineListenerAttached = true;
      window.addEventListener("online", () => {
        this.processQueue();
      });
      // Tenta esvaziar a fila imediatamente caso ja esteja online.
      this.processQueue();
    },

    /**
     * Carrega dados do backend se online. Retorna do cache se offline.
     */
    async loadProtocols() {
      if (!navigator.onLine) {
        return { success: true, fromCache: true };
      }

      try {
        const data = await loadDonationPickupProtocols({
          status: DONATION_PICKUP_PROTOCOL_STATUSES.SCHEDULED,
        });

        // Mantém protocolos que já estão na fila de sync e não vieram no payload
        // (ex: recém finalizados offline que ainda não subiram)
        const queuedProtocolIds = new Set(
          this.syncQueue.map((q) => q.payload?.protocol).filter(Boolean)
        );

        const mergedProtocols = [...data];
        this.protocols.forEach((p) => {
          if (
            queuedProtocolIds.has(p.protocol) &&
            !data.find((d) => d.id === p.id)
          ) {
            mergedProtocols.push(p);
          }
        });

        this.protocols = mergedProtocols;

        // Atualiza customerData em background
        this.loadAllCustomerData();

        return { success: true, fromCache: false };
      } catch (err) {
        console.error("Erro ao carregar do backend, usando cache", err);
        return { success: true, fromCache: true };
      }
    },

    async loadAllCustomerData() {
      if (!navigator.onLine) return;
      const promises = this.protocols.map(async (proto) => {
        try {
          const data = await loadDonationPickupCustomerData(
            proto.monthKey,
            proto.protocol
          );
          if (data) {
            this.customerDataMap[proto.id] = data;
          }
        } catch (e) {
          console.error("Erro customerData", e);
        }
      });
      await Promise.all(promises);
    },

    /**
     * Enfileira uma ação de coleta de item (salvamento otimista).
     */
    async enqueueItemResult(proto, item, pickupStatus, reason = null) {
      // 1. Atualiza cache local (Optimistic UI)
      const pIdx = this.protocols.findIndex((p) => p.id === proto.id);
      if (pIdx >= 0) {
        const iIdx = (this.protocols[pIdx].items || []).findIndex(
          (i) => i.id === item.id
        );
        if (iIdx >= 0) {
          this.protocols[pIdx].items[iIdx].pickupStatus = pickupStatus;
          this.protocols[pIdx].items[iIdx].pickupReason = reason;
        }
      }

      // 2. Adiciona à fila
      this.syncQueue.push({
        id: `action_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        type: "ITEM_RESULT",
        status: "pending",
        timestamp: new Date().toISOString(),
        payload: {
          monthKey: proto.monthKey,
          protocol: proto.protocol,
          itemId: item.id,
          pickupStatus,
          pickupReason: reason,
          forceDriverWins: true, // Flag para sobrepor regras da central
        },
      });

      this.processQueue();
    },

    /**
     * Enfileira a finalização do protocolo (assinatura).
     */
    async enqueueCompletePickup(
      proto,
      signatureCaptured,
      signaturePath,
      pdfAttachment,
      itemsState
    ) {
      // 1. Atualiza cache local
      const pIdx = this.protocols.findIndex((p) => p.id === proto.id);
      if (pIdx >= 0) {
        this.protocols[pIdx].status =
          DONATION_PICKUP_PROTOCOL_STATUSES.COLLECTED;
        this.removeProtocol(proto.id);
      }

      // 2. Adiciona à fila
      this.syncQueue.push({
        id: `action_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        type: "COMPLETE_PICKUP",
        status: "pending",
        timestamp: new Date().toISOString(),
        payload: {
          monthKey: proto.monthKey,
          protocol: proto.protocol,
          signatureCaptured,
          signaturePath, // Isso pode ser um base64 em modo offline
          pdfAttachment, // Base64 do PDF (se gerado)
          itemsState,
          forceDriverWins: true,
        },
      });

      this.processQueue();
    },

    /**
     * Enfileira reagendamento.
     */
    async enqueueReschedule(proto, reason) {
      const pIdx = this.protocols.findIndex((p) => p.id === proto.id);
      if (pIdx >= 0) {
        this.protocols[pIdx].status =
          DONATION_PICKUP_PROTOCOL_STATUSES.RESCHEDULE_PENDING;
        this.removeProtocol(proto.id);
      }
      this.syncQueue.push({
        id: `action_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        type: "RESCHEDULE",
        status: "pending",
        timestamp: new Date().toISOString(),
        payload: {
          monthKey: proto.monthKey,
          protocol: proto.protocol,
          reason,
          forceDriverWins: true,
        },
      });
      this.processQueue();
    },

    /**
     * Enfileira cancelamento.
     */
    async enqueueCancel(proto, reason) {
      const pIdx = this.protocols.findIndex((p) => p.id === proto.id);
      if (pIdx >= 0) {
        this.protocols[pIdx].status =
          DONATION_PICKUP_PROTOCOL_STATUSES.CANCELED_BY_CUSTOMER;
        this.removeProtocol(proto.id);
      }
      this.syncQueue.push({
        id: `action_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        type: "CANCEL",
        status: "pending",
        timestamp: new Date().toISOString(),
        payload: {
          monthKey: proto.monthKey,
          protocol: proto.protocol,
          reason,
          forceDriverWins: true,
        },
      });
      this.processQueue();
    },

    /**
     * Processa a fila enviando para o backend.
     */
    async processQueue() {
      if (this.isSyncing) return;
      if (!navigator.onLine) return;
      if (!this.syncQueue.length) return;

      this.isSyncing = true;
      try {
        const pendingItems = this.syncQueue.filter(
          (i) => i.status === "pending"
        );

        for (const item of pendingItems) {
          try {
            if (item.type === "ITEM_RESULT") {
              await recordDonationPickupItemResult(item.payload);
            } else if (item.type === "COMPLETE_PICKUP") {
              // A assinatura pode estar guardada como Base64 (capturada
              // offline). Antes de finalizar, sobe a imagem para o Storage e
              // troca o Base64 pelo caminho real. A troca fica persistida na
              // fila, entao uma eventual retentativa nao reenvia a imagem.
              if (
                typeof item.payload.signaturePath === "string" &&
                item.payload.signaturePath.startsWith("data:image/")
              ) {
                const result = await uploadDonationPickupSignatureDataUrl(
                  item.payload.signaturePath,
                  item.payload.monthKey,
                  item.payload.protocol
                );
                item.payload.signaturePath = result.path;
              }
              await completeDonationPickup(item.payload);
            } else if (item.type === "RESCHEDULE") {
              await markDonationPickupReschedulePending(item.payload);
            } else if (item.type === "CANCEL") {
              await cancelDonationPickupRequest(item.payload);
            }

            // Marca como sucesso
            item.status = "success";
          } catch (error) {
            console.error(`Erro ao sincronizar item ${item.id}`, error);
            // Mesmo com erro de rede ou timeout, mantemos pending para retentativa futura.
            // Erros críticos de backend devem ser marcados como error se não forem de rede.
            item.error = error.message;
            // Se for erro de rede, break loop para não travar próximos.
            if (
              error.message.includes("network") ||
              error.message.includes("offline")
            ) {
              break;
            }
          }
        }

        // Limpa sucessos
        this.syncQueue = this.syncQueue.filter((i) => i.status !== "success");
        this.lastSyncAt = new Date().toISOString();
      } finally {
        this.isSyncing = false;
      }
    },

    removeProtocol(protoId) {
      this.protocols = this.protocols.filter((p) => p.id !== protoId);
    },
  },
});
